import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaExternalLinkAlt } from 'react-icons/fa';
import PageSeo from '../components/PageSeo';
import Newsletters from './Newsletters';
import {
  CAPABILITY_PROOF,
  CLASSIC_GALLERY,
  EDUCATION_SNAPSHOTS,
  FEATURED_CASE_STUDIES,
  HERO_PROOF_NODES,
  MARKETPLACE_SNAPSHOTS,
  PORTFOLIO_EXPLORER_PROJECTS,
  PORTFOLIO_FILTERS,
  PORTFOLIO_PROOF_METRICS,
  hostLabel,
  projectMatchesFilter,
  type PortfolioFilterId,
  type SnapshotSite,
} from '../data/portfolioPremium';
import '../styles/portfolio-premium.css';

const SNAPSHOT_BADGE: Record<string, string> = {
  education: 'Education',
  marketplace: 'B2B marketplace',
};

function SnapshotCard({ site }: { site: SnapshotSite }) {
  const label = site.title || hostLabel(site.url);
  const badge = SNAPSHOT_BADGE[site.group] || 'Case study';

  return (
    <article className="portfolio-premium-snapshot">
      <span className="portfolio-premium-snapshot__badge">{badge}</span>
      <figure className="portfolio-premium-snapshot__thumb">
        <img
          src={site.image}
          alt={`Full-page snapshot of ${hostLabel(site.url)}`}
          loading="lazy"
          width={640}
          height={360}
        />
      </figure>
      <div className="portfolio-premium-snapshot__actions">
        <a
          className="portfolio-premium-snapshot__site"
          href={site.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {label}
          <FaExternalLinkAlt aria-hidden="true" />
        </a>
        <a
          href={`/${site.image}`}
          target="_blank"
          rel="noopener noreferrer"
          className="portfolio-premium-snapshot__png"
        >
          View full-page PNG
        </a>
      </div>
    </article>
  );
}

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState<PortfolioFilterId>('all');

  const filteredProjects = useMemo(
    () => PORTFOLIO_EXPLORER_PROJECTS.filter((p) => projectMatchesFilter(p, activeFilter)),
    [activeFilter],
  );

  return (
    <>
      <PageSeo path="/portfolio" />

      <div className="portfolio-premium">
        <div className="header-spacer" />

        {/* Hero */}
        <section className="portfolio-premium-hero" aria-labelledby="portfolio-premium-heading">
          <div className="portfolio-premium__shell">
            <div className="portfolio-premium-hero__layout">
              <div>
                <p className="portfolio-premium-hero__kicker">Proof-led delivery · Gautam Buddha Nagar, India</p>
                <h1 id="portfolio-premium-heading" className="portfolio-premium-hero__title">
                  Portfolio: web, ecommerce, AI, education, and integration work
                </h1>
                <p className="portfolio-premium-hero__lead">
                  We design, develop, integrate, and support production-ready systems for education,
                  ecommerce, marketplaces, platforms, and mobile — with full-page captures, stack-filtered
                  project proof, and long-term client support across regions.
                </p>
                <div className="portfolio-premium-hero__actions">
                  <Link to="/getintouch" className="portfolio-premium-btn portfolio-premium-btn--primary">
                    Discuss a similar project
                  </Link>
                  <a href="#portfolio-work" className="portfolio-premium-btn portfolio-premium-btn--ghost">
                    Browse project proof
                  </a>
                </div>
              </div>

              <div className="portfolio-premium-nodes" aria-hidden="true">
                {HERO_PROOF_NODES.map((node) => (
                  <div key={node} className="portfolio-premium-nodes__item">
                    {node}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Proof metrics */}
        <section className="portfolio-premium-proof" aria-labelledby="portfolio-proof-heading">
          <div className="portfolio-premium__shell">
            <h2 id="portfolio-proof-heading" className="sr-only">
              Portfolio delivery proof
            </h2>
            <div className="portfolio-premium-proof__grid">
              {PORTFOLIO_PROOF_METRICS.map((item) => (
                <article key={item.id} className="portfolio-premium-proof__card">
                  <span className="portfolio-premium-proof__value">{item.value}</span>
                  <span className="portfolio-premium-proof__label">{item.label}</span>
                  <p className="portfolio-premium-proof__detail">{item.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Featured case studies */}
        <section className="portfolio-premium-section" aria-labelledby="portfolio-featured-heading">
          <div className="portfolio-premium__shell">
            <div className="portfolio-premium-section__header">
              <p className="portfolio-premium-section__kicker">Featured proof</p>
              <h2 id="portfolio-featured-heading" className="portfolio-premium-section__title">
                Case studies across education, marketplace, ecommerce, AI, and platforms
              </h2>
              <p className="portfolio-premium-section__intro">
                Selected projects that show how we contribute — UX, engineering, integrations, and
                support — not just screenshots.
              </p>
            </div>
            <div className="portfolio-premium-featured">
              {FEATURED_CASE_STUDIES.map((study) => (
                <article
                  key={study.id}
                  className={`portfolio-premium-featured__card portfolio-premium-featured__card--${study.layout}`}
                >
                  <figure className="portfolio-premium-featured__thumb">
                    <span className="portfolio-premium-featured__badge">{study.category}</span>
                    <img src={study.image} alt={study.alt} loading="lazy" width={640} height={360} />
                  </figure>
                  {study.supportingImages && study.supportingImages.length > 0 ? (
                    <div className="portfolio-premium-featured__support" aria-label={`${study.title} related project screenshots`}>
                      {study.supportingImages.map((image) => (
                        <figure key={image.src} className="portfolio-premium-featured__support-item">
                          <img src={image.src} alt={image.alt} loading="lazy" width={220} height={140} />
                        </figure>
                      ))}
                    </div>
                  ) : null}
                  <div className="portfolio-premium-featured__body">
                    <div className="portfolio-premium-featured__meta">
                      <span className="portfolio-premium-featured__stack">{study.stack}</span>
                    </div>
                    <h3 className="portfolio-premium-featured__title">{study.title}</h3>
                    <p className="portfolio-premium-featured__summary">{study.summary}</p>
                    <ul className="portfolio-premium-featured__proof">
                      {study.proofPoints.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                    <a
                      href={study.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="portfolio-premium-featured__link"
                    >
                      View live project <FaExternalLinkAlt aria-hidden="true" />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Filterable project explorer */}
        <section
          id="portfolio-work"
          className="portfolio-premium-section portfolio-premium-section--dark"
          aria-labelledby="portfolio-explorer-heading"
        >
          <div className="portfolio-premium__shell">
            <div className="portfolio-premium-section__header">
              <p className="portfolio-premium-section__kicker">Project explorer</p>
              <h2 id="portfolio-explorer-heading" className="portfolio-premium-section__title">
                Filter projects by industry and technology
              </h2>
              <p className="portfolio-premium-section__intro">
                Browse Magento, React, WordPress, Laravel, CodeIgniter, React Native, ecommerce,
                marketplace, AI, and platform work — with stack tags and delivery context.
              </p>
            </div>

            <ul className="portfolio-premium-filters" aria-label="Filter portfolio projects">
              {PORTFOLIO_FILTERS.map(({ id, label }) => {
                const selected = activeFilter === id;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      aria-pressed={selected}
                      className={`portfolio-premium-filters__btn${selected ? ' is-active' : ''}`}
                      onClick={() => setActiveFilter(id)}
                    >
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="portfolio-premium-explorer">
              {filteredProjects.length === 0 ? (
                <p className="portfolio-premium-explorer__empty" role="status">
                  No projects match this filter. Try another category or stack.
                </p>
              ) : (
                filteredProjects.map((project) => (
                  <article key={project.id} className="portfolio-premium-explorer__card">
                    <a
                      className="portfolio-premium-explorer__link"
                      href={project.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <figure className="portfolio-premium-explorer__thumb">
                        <img src={project.image} alt={project.alt} loading="lazy" width={400} height={260} />
                      </figure>
                      <div className="portfolio-premium-explorer__body">
                        <h3 className="portfolio-premium-explorer__title">{project.title}</h3>
                        <p className="portfolio-premium-explorer__summary">{project.summary}</p>
                        <div className="portfolio-premium-explorer__tags">
                          {project.stackLabel && (
                            <span className="portfolio-premium-explorer__tag">{project.stackLabel}</span>
                          )}
                          {project.services.slice(0, 2).map((service) => (
                            <span key={service} className="portfolio-premium-explorer__tag">
                              {service}
                            </span>
                          ))}
                        </div>
                      </div>
                    </a>
                  </article>
                ))
              )}
            </div>
          </div>
        </section>

        {/* Education snapshot wall */}
        <section className="portfolio-premium-section portfolio-premium-section--dark" aria-labelledby="portfolio-snapshots-heading">
          <div className="portfolio-premium__shell">
            <div className="portfolio-premium-section__header">
              <p className="portfolio-premium-section__kicker">Global education proof</p>
              <h2 id="portfolio-snapshots-heading" className="portfolio-premium-section__title">
                Full-page captures of education and marketplace sites
              </h2>
              <p className="portfolio-premium-section__intro">
                Desktop snapshots after cookie consent. Visit the live site or open the full-page PNG
                for detailed review.
              </p>
            </div>
            <div className="portfolio-premium-snapshots">
              {EDUCATION_SNAPSHOTS.map((site) => (
                <SnapshotCard key={site.url} site={site} />
              ))}
              {MARKETPLACE_SNAPSHOTS.map((site) => (
                <SnapshotCard key={site.url} site={site} />
              ))}
            </div>
          </div>
        </section>

        {/* Capability proof */}
        <section className="portfolio-premium-section" aria-labelledby="portfolio-capabilities-heading">
          <div className="portfolio-premium__shell">
            <div className="portfolio-premium-section__header">
              <p className="portfolio-premium-section__kicker">How we contribute</p>
              <h2 id="portfolio-capabilities-heading" className="portfolio-premium-section__title">
                Technology and integration proof across every engagement
              </h2>
              <p className="portfolio-premium-section__intro">
                UX, CMS, ecommerce, APIs, CRM/forms, AI, mobile, analytics, and long-term support —
                mapped to how we actually deliver projects.
              </p>
            </div>
            <div className="portfolio-premium-capabilities">
              {CAPABILITY_PROOF.map((item) => (
                <article key={item.id} className="portfolio-premium-capability">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Classic gallery archive */}
        <section className="portfolio-premium-archive" aria-labelledby="portfolio-archive-heading">
          <div className="portfolio-premium__shell">
            <div className="portfolio-premium-archive__panel">
              <div className="portfolio-premium-archive__copy">
                <h2 id="portfolio-archive-heading">Classic technology gallery</h2>
                <p>{CLASSIC_GALLERY.description}</p>
              </div>
              <Link to={CLASSIC_GALLERY.to} className="portfolio-premium-btn portfolio-premium-btn--secondary">
                {CLASSIC_GALLERY.label} <FaArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="portfolio-premium-cta" aria-labelledby="portfolio-cta-heading">
          <div className="portfolio-premium__shell">
            <div className="portfolio-premium-cta__panel">
              <div className="portfolio-premium-cta__copy">
                <h2 id="portfolio-cta-heading">
                  Need a team that can deliver similar work with clear scope and proof?
                </h2>
                <p>
                  We can help with architecture, delivery, integrations, optimization, and long-term
                  support — starting with a clear technical conversation about your project goals.
                </p>
              </div>
              <div className="portfolio-premium-cta__actions">
                <Link to="/getintouch" className="portfolio-premium-btn portfolio-premium-btn--primary">
                  Talk to our technical team <FaArrowRight aria-hidden="true" />
                </Link>
                <Link to="/services" className="portfolio-premium-btn portfolio-premium-btn--secondary">
                  View services
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Newsletters />
      </div>
    </>
  );
};

export default Portfolio;
